import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'

import dayjs from 'utils/dayjs'
import { isCustomPeriodFilter, isPeriodFilterEnum } from './functions'
import { QueryParams } from 'redux/api/Widgets'

/**
 * Get from and to date based on a predfined period filter.
 * @param filter - Period filter enum.
 * @returns from date and to date on YYYY-MM-DD format.
 */
export function getDatesOfPeriod(filter: PeriodFilter): {
  from: string | null
  to: string | null
} {
  const today = dayjs()

  switch (filter) {
  case PeriodFilter.THIS_WEEK_TO_TODAY:
    return {
      from: today.startOf('week').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.THIS_MONTH_TO_TODAY:
    return {
      from: today.startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.THIS_QUARTER_TO_TODAY:
    return {
      from: today.startOf('quarter').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.THIS_YEAR_TO_TODAY:
    return {
      from: today.startOf('year').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_2_YEARS_TO_TODAY:
    return {
      from: today.subtract(2, 'year').startOf('year').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_3_YEARS_TO_TODAY:
    return {
      from: today.subtract(3, 'year').startOf('year').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.TODAY:
    return {
      from: today.format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.YESTERDAY:
    return {
      from: today.subtract(1, 'day').format('YYYY-MM-DD'),
      to: today.subtract(1, 'day').format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_WEEK:
    return {
      from: today.subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
      to: today.subtract(1, 'week').endOf('week').format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_MONTH:
    return {
      from: today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      to: today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_QUARTER:
    return {
      from: today
        .subtract(1, 'quarter')
        .startOf('quarter')
        .format('YYYY-MM-DD'),
      to: today.subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_YEAR:
    return {
      from: today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      to: today.subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_7_DAYS:
    return {
      from: today.subtract(6, 'days').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_30_DAYS:
    return {
      from: today.subtract(29, 'days').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_90_DAYS:
    return {
      from: today.subtract(89, 'days').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_365_DAYS:
    return {
      from: today.subtract(364, 'days').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_3_MONTHS_TO_TODAY:
    return {
      from: today.subtract(2, 'month').startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_6_MONTHS_TO_TODAY:
    return {
      from: today.subtract(5, 'month').startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_12_MONTHS_TO_TODAY:
    return {
      from: today.subtract(11, 'month').startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_24_MONTHS_TO_TODAY:
    return {
      from: today.subtract(23, 'month').startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_36_MONTHS_TO_TODAY:
    return {
      from: today.subtract(35, 'month').startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_4_QUARTERS_TO_TODAY:
    return {
      from: today
        .subtract(3, 'quarter')
        .startOf('quarter')
        .format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_8_QUARTERS_TO_TODAY:
    return {
      from: today
        .subtract(7, 'quarter')
        .startOf('quarter')
        .format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  case PeriodFilter.LAST_12_QUARTERS_TO_TODAY:
    return {
      from: today
        .subtract(11, 'quarter')
        .startOf('quarter')
        .format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD')
    }
  default:
    return {
      from: null,
      to: null
    }
  }
}

export function getDatePeriodFromPeriodFilter(
  periodFilter: PeriodFilter | CustomPeriodFilter
): { from: string; to: string } {
  const period = isPeriodFilterEnum(periodFilter)
    ? getDatesOfPeriod(periodFilter)
    : periodFilter

  if (!period.from || !period.to)
    throw new Error(
      `Invalid period: missing from/to. Got: ${JSON.stringify(period)}`
    )

  return period as { from: string; to: string }
}

export function parsePeriodFilter(
  periodFilter: PeriodFilter | CustomPeriodFilter | null
): Pick<QueryParams, 'period_filter' | 'from_date' | 'to_date'> | null {
  if (
    periodFilter &&
    isCustomPeriodFilter(periodFilter) &&
    periodFilter.from &&
    periodFilter.to
  ) {
    return {
      from_date: periodFilter.from,
      to_date: periodFilter.to
    }
  }
  if (periodFilter && isPeriodFilterEnum(periodFilter)) {
    return {
      period_filter: periodFilter
    }
  }

  return null
}
